import axios from 'axios';

// Base URL for monolithic backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Get token from localStorage
const getToken = () => {
  return localStorage.getItem('token');
};

// Create axios instance with auth header
const createAxiosInstance = () => {
  const instance = axios.create({ baseURL: API_URL });

  instance.interceptors.request.use(
    (config) => {
      const token = getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return instance;
};

// User Service API
export const userAPI = {
  register: (data) => axios.post(`${API_URL}/users/register`, data),
  login: (data) => axios.post(`${API_URL}/users/login`, data),
  forgotPassword: (data) => axios.post(`${API_URL}/users/forgot-password`, data),
  resetPassword: (data) => axios.post(`${API_URL}/users/reset-password`, data),
  changePassword: (data) => createAxiosInstance().put('/users/change-password', data),
  setPassword: (data) => createAxiosInstance().put('/users/set-password', data),
  getMe: () => createAxiosInstance().get('/users/me'),
  getUserById: (id) => createAxiosInstance().get(`/users/${id}`),
  updateUser: (id, data) => createAxiosInstance().put(`/users/${id}`, data),
  uploadPhoto: (id, formData) => createAxiosInstance().post(`/users/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Travels Info (for owners)
  updateTravelsInfo: (data) => createAxiosInstance().put('/users/travels-info', data),
  uploadTravelsLogo: (formData) => createAxiosInstance().post('/users/travels-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadTravelsCover: (formData) => createAxiosInstance().post('/users/travels-cover', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getTravelsInfo: (ownerId) => createAxiosInstance().get(`/users/travels-info/${ownerId}`),

  // Cloudinary Config (for owners)
  updateCloudinaryConfig: (data) => createAxiosInstance().put('/users/cloudinary-config', data),
  getCloudinaryConfig: () => createAxiosInstance().get('/users/cloudinary-config'),

  // Staff Management (for owners)
  getAvailableRoles: () => createAxiosInstance().get('/users/roles'),
  createStaff: (data) => createAxiosInstance().post('/users/staff', data),
  getStaffMembers: (params) => createAxiosInstance().get('/users/staff', { params }),
  getStaffById: (id) => createAxiosInstance().get(`/users/staff/${id}`),
  updateStaff: (id, data) => createAxiosInstance().put(`/users/staff/${id}`, data),
  deleteStaff: (id) => createAxiosInstance().delete(`/users/staff/${id}`),
  updateStaffPermissions: (id, permissions) => createAxiosInstance().put(`/users/staff/${id}/permissions`, { permissions }),
  resetStaffPassword: (id, newPassword) => createAxiosInstance().put(`/users/staff/${id}/reset-password`, { newPassword }),

  // Staff Photo & Documents
  uploadStaffPhoto: (id, formData) => createAxiosInstance().post(`/users/staff/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteStaffPhoto: (id) => createAxiosInstance().delete(`/users/staff/${id}/photo`),
  uploadStaffDocument: (id, formData) => createAxiosInstance().post(`/users/staff/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getStaffDocuments: (id) => createAxiosInstance().get(`/users/staff/${id}/documents`),
  deleteStaffDocument: (id, documentId) => createAxiosInstance().delete(`/users/staff/${id}/documents/${documentId}`),

  // App Settings
  getAppSettings: () => createAxiosInstance().get('/users/app-settings'),
  updateAppSettings: (data) => createAxiosInstance().put('/users/app-settings', data),

  // Admin - Owners Stats
  getOwnersStats: () => createAxiosInstance().get('/users/admin/owners-stats'),
  notifyOwners: (data) => createAxiosInstance().post('/users/admin/notify-owners', data)
};

// Car Service API
export const carAPI = {
  addCar: (data) => createAxiosInstance().post('/cars/add', data),
  createCar: (data) => createAxiosInstance().post('/cars/add', data),
  bulkImport: (vehicles) => createAxiosInstance().post('/cars/bulk-import', { vehicles }),
  getAllCars: (includeDeleted = false) => createAxiosInstance().get('/cars/all', { params: { includeDeleted } }),
  getCarsByOwner: (ownerId) => createAxiosInstance().get(`/cars/owner/${ownerId}`),
  getAvailableCars: () => createAxiosInstance().get('/cars/available'),
  getCarById: (carId) => createAxiosInstance().get(`/cars/${carId}`),
  updateCar: (carId, data) => createAxiosInstance().put(`/cars/${carId}`, data),
  deleteCar: (carId) => createAxiosInstance().delete(`/cars/${carId}`),
  restoreCar: (carId) => createAxiosInstance().put(`/cars/${carId}/restore`),
  getDeletedCars: () => createAxiosInstance().get('/cars/deleted'),
  permanentDeleteCar: (carId) => createAxiosInstance().delete(`/cars/${carId}/permanent`),
  uploadImages: (carId, formData) => {
    const instance = createAxiosInstance();
    return instance.post(`/cars/upload/${carId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  },
  deleteImage: (carId, imageUrl) => createAxiosInstance().post(`/cars/${carId}/images/delete`, { imageUrl }),

  // Service/Maintenance
  addService: (carId, data) => createAxiosInstance().post(`/cars/${carId}/services`, data),
  getServices: (carId, params) => createAxiosInstance().get(`/cars/${carId}/services`, { params }),
  getServiceById: (carId, serviceId) => createAxiosInstance().get(`/cars/${carId}/services/${serviceId}`),
  getUpcomingServices: (carId) => createAxiosInstance().get(`/cars/${carId}/services/upcoming`),
  updateService: (carId, serviceId, data) => createAxiosInstance().put(`/cars/${carId}/services/${serviceId}`, data),
  deleteService: (carId, serviceId) => createAxiosInstance().delete(`/cars/${carId}/services/${serviceId}`),

  // Expenses
  addExpense: (carId, data) => createAxiosInstance().post(`/cars/${carId}/expenses`, data),
  getExpenses: (carId, params) => createAxiosInstance().get(`/cars/${carId}/expenses`, { params }),
  getExpenseSummary: (carId, params) => createAxiosInstance().get(`/cars/${carId}/expenses/summary`, { params }),
  updateExpense: (carId, expenseId, data) => createAxiosInstance().put(`/cars/${carId}/expenses/${expenseId}`, data),
  deleteExpense: (carId, expenseId) => createAxiosInstance().delete(`/cars/${carId}/expenses/${expenseId}`),

  // FASTag
  getFastag: (carId) => createAxiosInstance().get(`/cars/${carId}/fastag`),
  updateFastag: (carId, data) => createAxiosInstance().put(`/cars/${carId}/fastag`, data),
  addFastagTransaction: (carId, data) => createAxiosInstance().post(`/cars/${carId}/fastag/transactions`, data),
  getFastagTransactions: (carId, params) => createAxiosInstance().get(`/cars/${carId}/fastag/transactions`, { params }),
  deleteFastagTransaction: (carId, transactionId) => createAxiosInstance().delete(`/cars/${carId}/fastag/transactions/${transactionId}`)
};

// Booking Service API
export const bookingAPI = {
  createBooking: (data) => createAxiosInstance().post('/bookings/create', data),
  getAllBookings: (params) => createAxiosInstance().get('/bookings', { params }),
  getBookingsByOwner: (ownerId) => createAxiosInstance().get(`/bookings/owner/${ownerId}`),
  getBookingsByCustomer: (customerId) => createAxiosInstance().get(`/bookings/customer/${customerId}`),
  getBookingById: (bookingId) => createAxiosInstance().get(`/bookings/${bookingId}`),
  updateBookingStatus: (bookingId, status, rejectionReason, paymentStatus) => createAxiosInstance().put(`/bookings/status/${bookingId}`, { status, rejectionReason, paymentStatus }),
  cancelBooking: (bookingId) => createAxiosInstance().delete(`/bookings/${bookingId}`),
  // Trip lifecycle (owner-as-driver)
  startTrip: (bookingId) => createAxiosInstance().put(`/bookings/${bookingId}/start-trip`),
  completeTrip: (bookingId, data) => {
    if (data instanceof FormData) {
      return createAxiosInstance().put(`/bookings/${bookingId}/complete-trip`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return createAxiosInstance().put(`/bookings/${bookingId}/complete-trip`, data || {});
  },
  recordPayment: (bookingId, data) => {
    if (data instanceof FormData) {
      return createAxiosInstance().put(`/bookings/${bookingId}/record-payment`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    }
    return createAxiosInstance().put(`/bookings/${bookingId}/record-payment`, data || {});
  },
  // Driver assignment
  assignDriver: (bookingId, driverId) => createAxiosInstance().put(`/bookings/${bookingId}/assign-driver`, { driverId }),
  unassignDriver: (bookingId) => createAxiosInstance().put(`/bookings/${bookingId}/unassign-driver`),
  getMyAssignments: () => createAxiosInstance().get('/bookings/my-assignments')
};

// Maintenance Service API
export const maintenanceAPI = {
  scheduleMaintenance: (data) => createAxiosInstance().post('/maintenance/schedule', data),
  recordMaintenance: (data) => createAxiosInstance().post('/maintenance/record', data),
  getMaintenanceHistory: (carId) => createAxiosInstance().get(`/maintenance/history/${carId}`),
  getUpcomingMaintenance: (carId) => createAxiosInstance().get(`/maintenance/upcoming/${carId}`),
  updateMaintenanceStatus: (data) => createAxiosInstance().put('/maintenance/update-status', data)
};

// Notification Service API
export const notificationAPI = {
  sendNotification: (data) => createAxiosInstance().post('/notifications/send', data),
  getUserNotifications: (userId) => createAxiosInstance().get(`/notifications/user/${userId}`),
  markAsRead: (notificationId) => createAxiosInstance().put(`/notifications/${notificationId}/read`),
  markAllAsRead: (userId) => createAxiosInstance().put(`/notifications/user/${userId}/read-all`),
  getUnreadCount: (userId) => createAxiosInstance().get(`/notifications/user/${userId}/unread-count`)
};

// Car Document API
export const documentAPI = {
  uploadDocument: (formData) => createAxiosInstance().post('/car-documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getCarDocuments: (carId) => createAxiosInstance().get(`/car-documents/car/${carId}`),
  getExpiringDocuments: () => createAxiosInstance().get('/car-documents/expiring'),
  updateDocument: (id, formData) => createAxiosInstance().put(`/car-documents/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDocument: (id) => createAxiosInstance().delete(`/car-documents/${id}`)
};

// Car Photo API
export const photoAPI = {
  uploadPhotos: (formData) => createAxiosInstance().post('/car-photos/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getCarPhotos: (carId, category) => createAxiosInstance().get(`/car-photos/car/${carId}`, { params: { category } }),
  updatePhoto: (id, data) => createAxiosInstance().put(`/car-photos/${id}`, data),
  setPrimaryPhoto: (id) => createAxiosInstance().patch(`/car-photos/${id}/primary`),
  reorderPhotos: (photoOrders) => createAxiosInstance().patch('/car-photos/reorder', { photoOrders }),
  deletePhoto: (id) => createAxiosInstance().delete(`/car-photos/${id}`)
};

// Vehicle Inspection API
export const inspectionAPI = {
  createInspection: (data) => createAxiosInstance().post('/inspections', data),
  getCarInspections: (carId, params) => createAxiosInstance().get(`/inspections/car/${carId}`, { params }),
  getInspectionStats: (carId) => createAxiosInstance().get(`/inspections/car/${carId}/stats`),
  getBookingInspections: (bookingId) => createAxiosInstance().get(`/inspections/booking/${bookingId}`),
  getInspectionById: (id) => createAxiosInstance().get(`/inspections/${id}`),
  updateInspection: (id, data) => createAxiosInstance().put(`/inspections/${id}`, data),
  addDamageReport: (id, data) => createAxiosInstance().post(`/inspections/${id}/damage`, data),
  getInspectionsRequiringRepair: () => createAxiosInstance().get('/inspections/repairs'),
  deleteInspection: (id) => createAxiosInstance().delete(`/inspections/${id}`)
};

// Waitlist API
export const waitlistAPI = {
  joinWaitlist: (data) => createAxiosInstance().post('/waitlist', data),
  getMyWaitlist: (status) => createAxiosInstance().get('/waitlist/my', { params: { status } }),
  getCarWaitlist: (carId, params) => createAxiosInstance().get(`/waitlist/car/${carId}`, { params }),
  notifyWaitlist: (data) => createAxiosInstance().post('/waitlist/notify', data),
  convertToBooking: (id, bookingId) => createAxiosInstance().post(`/waitlist/${id}/convert`, { bookingId }),
  cancelWaitlist: (id) => createAxiosInstance().delete(`/waitlist/${id}`),
  updatePriority: (id, priority) => createAxiosInstance().patch(`/waitlist/${id}/priority`, { priority })
};

// Journey API
export const journeyAPI = {
  getAllJourneys: (params) => createAxiosInstance().get('/journeys', { params }),
  getMyJourneys: () => createAxiosInstance().get('/journeys/my'),
  getActiveJourneys: () => createAxiosInstance().get('/journeys/active'),
  getJourneyById: (id) => createAxiosInstance().get(`/journeys/${id}`),
  getJourneysByCar: (carId) => createAxiosInstance().get(`/journeys/car/${carId}`),
  getJourneysByDriver: (driverId) => createAxiosInstance().get(`/journeys/driver/${driverId}`),
  createJourney: (data) => createAxiosInstance().post('/journeys/create', data),
  completeJourney: (id, data) => createAxiosInstance().put(`/journeys/${id}/complete`, data),
  addIncident: (id, data) => createAxiosInstance().post(`/journeys/${id}/incident`, data),

  // Live Tracking APIs
  startJourney: (id, data) => createAxiosInstance().put(`/journeys/${id}/start`, data),
  endJourney: (id, data) => createAxiosInstance().put(`/journeys/${id}/end`, data),
  updateLocation: (id, data) => createAxiosInstance().put(`/journeys/${id}/update-location`, data),
  updateKmReading: (id, data) => createAxiosInstance().put(`/journeys/${id}/update-km`, data),
  addCheckpoint: (id, data) => createAxiosInstance().put(`/journeys/${id}/add-checkpoint`, data),
  toggleTracking: (id, data) => createAxiosInstance().put(`/journeys/${id}/toggle-tracking`, data),
  getLiveLocation: (id) => createAxiosInstance().get(`/journeys/${id}/live-location`),
  getActiveJourneyForBooking: (bookingId) => createAxiosInstance().get(`/journeys/booking/${bookingId}/active`),

  // Public tracking (no auth)
  trackByCode: (trackingCode) => axios.get(`${API_URL}/journeys/track/${trackingCode}`),

  // Update journey costs (admin only)
  updateCosts: (id, data) => createAxiosInstance().put(`/journeys/${id}/update-costs`, data),

  // Update journey details (distance, duration, fuel, odometer)
  updateDetails: (id, data) => createAxiosInstance().put(`/journeys/${id}/update-details`, data),

  // Upload odometer photo
  uploadOdometerPhoto: (id, formData) => createAxiosInstance().post(`/journeys/${id}/odometer-photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Update payment status
  updatePaymentStatus: (id, data) => createAxiosInstance().put(`/journeys/${id}/payment-status`, data)
};

// Reports API
export const reportsAPI = {
  getBookingsReport: (params) => createAxiosInstance().get('/reports/bookings', { params }),
  getRevenueReport: (params) => createAxiosInstance().get('/reports/revenue', { params }),
  getCarUtilizationReport: (params) => createAxiosInstance().get('/reports/car-utilization', { params }),
  getCustomerReport: (params) => createAxiosInstance().get('/reports/customers', { params }),
  getInspectionReport: (params) => createAxiosInstance().get('/reports/inspections', { params }),
  getDashboardSummary: () => createAxiosInstance().get('/reports/dashboard-summary'),
  getDashboardAlerts: () => createAxiosInstance().get('/reports/dashboard-alerts'),
  downloadBookingsCSV: (params) => createAxiosInstance().get('/reports/bookings', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
  downloadRevenueCSV: (params) => createAxiosInstance().get('/reports/revenue', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
  downloadCarUtilizationCSV: (params) => createAxiosInstance().get('/reports/car-utilization', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
  downloadCustomerCSV: (params) => createAxiosInstance().get('/reports/customers', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
  downloadInspectionCSV: (params) => createAxiosInstance().get('/reports/inspections', {
    params: { ...params, format: 'csv' },
    responseType: 'blob'
  }),
  getOwnerComparisonReport: (params) => createAxiosInstance().get('/reports/by-owner', { params })
};

// Platform Settings API (public GET, admin PUT)
export const platformAPI = {
  getSettings: () => axios.get(`${API_URL}/platform-settings`),
  updateSettings: (data) => createAxiosInstance().put('/platform-settings', data)
};

// One-Way Trip API
export const onewayTripAPI = {
  getAll: (params) => axios.get(`${API_URL}/oneway-trips`, { params }),
  getMy: (params) => createAxiosInstance().get('/oneway-trips/my', { params }),
  getById: (id) => axios.get(`${API_URL}/oneway-trips/details/${id}`),
  create: (data) => createAxiosInstance().post('/oneway-trips', data),
  update: (id, data) => createAxiosInstance().put(`/oneway-trips/${id}`, data),
  delete: (id) => createAxiosInstance().delete(`/oneway-trips/${id}`),
  book: (id, data) => createAxiosInstance().post(`/oneway-trips/${id}/book`, data)
};

// Packages API
export const packageAPI = {
  getMy: (params) => createAxiosInstance().get('/packages', { params }),
  getPublic: (ownerId) => axios.get(`${API_URL}/packages/owner-public/${ownerId}`),
  create: (data) => createAxiosInstance().post('/packages', data),
  update: (id, data) => createAxiosInstance().put(`/packages/${id}`, data),
  delete: (id) => createAxiosInstance().delete(`/packages/${id}`)
};

// Reviews API
export const reviewAPI = {
  getPublic: (ownerId) => axios.get(`${API_URL}/reviews/owner-public/${ownerId}`),
  submit: (ownerId, data) => axios.post(`${API_URL}/reviews/owner/${ownerId}`, data),
  getMy: () => createAxiosInstance().get('/reviews'),
  delete: (id) => createAxiosInstance().delete(`/reviews/${id}`)
};

// Default export
const api = createAxiosInstance();
export default api;
